/**
 * Wraps the browser's Geolocation API in a promise so the clock-in / clock-out
 * flows can `await` a one-shot fix. Returns `null` when:
 *   - the browser doesn't support geolocation
 *   - the user denies the permission prompt
 *   - the device fails to acquire a fix within the timeout
 *
 * The caller decides what to do on null (we let staff still clock in/out, the
 * server just stores `null` coords for that event).
 */
export interface CapturedLocation {
  latitude: number;
  longitude: number;
  accuracyMetres: number;
}

export interface GeolocationCaptureOptions {
  /** Max time to wait for a fix, in milliseconds. Default 12 000. */
  timeoutMs?: number;
  /** Reject cached fixes older than this (ms). Default 60 000. */
  maxAgeMs?: number;
  /** Request the most accurate fix the device can give. Default true. */
  highAccuracy?: boolean;
}

export async function captureCurrentLocation(
  opts: GeolocationCaptureOptions = {},
): Promise<CapturedLocation | null> {
  if (typeof navigator === 'undefined' || !navigator.geolocation) {
    return null;
  }

  // maxAgeMs defaults to 0 so every clock action triggers a fresh GPS fix —
  // otherwise back-to-back clock-in/out within ~60s would receive the cached
  // first reading and look identical even when the staff moved.
  const { timeoutMs = 12_000, maxAgeMs = 0, highAccuracy = true } = opts;

  return new Promise<CapturedLocation | null>((resolve) => {
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({
        latitude: pos.coords.latitude,
        longitude: pos.coords.longitude,
        accuracyMetres: pos.coords.accuracy,
      }),
      () => resolve(null),
      { enableHighAccuracy: highAccuracy, timeout: timeoutMs, maximumAge: maxAgeMs },
    );
  });
}
